import React, { useState, useEffect, useCallback } from 'react';
import AdminShell from '../components/layout/AdminShell';
import { PageHeader, CollapsibleSection, LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../services/api';
import { toast } from 'sonner';
import { Plus, Trash2, GripVertical, Settings, Users, ChevronUp, ChevronDown } from 'lucide-react';

export default function ContractSettingsPage() {
  const [settings, setSettings] = useState({
    require_approval: true,
    default_approval_route: [],
    notify_by_email: true,
  });
  const [allUsers, setAllUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, usersRes] = await Promise.all([
        api.get('/api/contracts/admin-settings'),
        api.get('/api/users', { params: { page_size: 200, role: 'licensed' } }),
      ]);
      setSettings(settingsRes.data);
      setAllUsers(usersRes.data.data || []);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/api/contracts/admin-settings', settings);
      toast.success('Contract settings saved');
    } catch (err) { if (err?.response?.status >= 500) toast.error('Save failed'); }
    setSaving(false);
  };

  const addApprover = (user) => {
    if (settings.default_approval_route.find(a => a.user_id === user.user_id)) return;
    const approver = {
      user_id: user.user_id,
      name: `${user.first_name} ${user.last_name}`.trim(),
      email: user.email,
      order: settings.default_approval_route.length + 1,
    };
    setSettings(prev => ({ ...prev, default_approval_route: [...prev.default_approval_route, approver] }));
  };

  const removeApprover = (userId) => {
    setSettings(prev => ({
      ...prev,
      default_approval_route: prev.default_approval_route
        .filter(a => a.user_id !== userId)
        .map((a, i) => ({ ...a, order: i + 1 })),
    }));
  };

  const filteredUsers = allUsers.filter(u => {
    const s = search.toLowerCase();
    return !s || `${u.first_name} ${u.last_name} ${u.user_id}`.toLowerCase().includes(s);
  });

  if (loading) return <AdminShell><div className="py-24"><LoadingSpinner /></div></AdminShell>;

  return (
    <AdminShell>
      <PageHeader
        title="Contract Settings"
        description="Configure contract approval requirements and default approval routes."
        actions={
          <Button onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        }
      />

      {/* Approval Toggle */}
      <CollapsibleSection title="Approval Requirements">
        <div className="space-y-4">
          <div className="flex items-start gap-4 p-4 rounded-xl border bg-muted/20">
            <Switch
              checked={settings.require_approval}
              onCheckedChange={v => setSettings(p => ({ ...p, require_approval: v }))}
              id="req-approval"
            />
            <div>
              <Label htmlFor="req-approval" className="text-sm font-semibold">Require Contract Approval</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When enabled, all contracts must go through the approval workflow before becoming active.
                When disabled, contracts can be marked active immediately.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 rounded-xl border bg-muted/20">
            <Switch
              checked={settings.notify_by_email}
              onCheckedChange={v => setSettings(p => ({ ...p, notify_by_email: v }))}
              id="notify-email"
            />
            <div>
              <Label htmlFor="notify-email" className="text-sm font-semibold">Notify Approvers by Email</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                When enabled, approvers receive an email notification in addition to the in-system notification.
                (Requires SMTP configuration — contact your system administrator.)
              </p>
            </div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Default Approval Route */}
      <CollapsibleSection title="Default Approval Route" className="mt-4">
        <div className="grid grid-cols-2 gap-6">
          {/* Route builder */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Current Default Route</h3>
            {settings.default_approval_route.length === 0 ? (
              <div className="border-2 border-dashed rounded-xl p-6 text-center">
                <Users className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No default approvers set.</p>
                <p className="text-xs text-muted-foreground">Add users from the right panel.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {settings.default_approval_route.map((approver, i) => (
                  <div key={approver.user_id} className="flex items-center gap-3 p-3 border rounded-xl bg-card">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary flex-shrink-0">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{approver.name}</p>
                      <p className="text-xs text-muted-foreground">{approver.email}</p>
                    </div>
                    {i < settings.default_approval_route.length - 1 && (
                      <span className="text-xs text-muted-foreground">then ↓</span>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => removeApprover(approver.user_id)}
                      aria-label={`Remove ${approver.name}`}>
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                ))}
                <div className="mt-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200">
                  <p className="text-xs text-green-700">
                    ✅ Sequential approval: contracts must be approved by each person in order.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* User picker */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Add Approvers</h3>
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search users..."
              className="h-8 mb-3"
            />
            <div className="max-h-64 overflow-y-auto space-y-1 border rounded-xl p-2">
              {filteredUsers.map(u => {
                const alreadyAdded = settings.default_approval_route.find(a => a.user_id === u.user_id);
                return (
                  <div key={u.user_id} className="flex items-center justify-between p-2 rounded-lg hover:bg-accent">
                    <div>
                      <p className="text-sm font-medium">{u.first_name} {u.last_name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <Button size="sm" variant={alreadyAdded ? 'secondary' : 'outline'}
                      disabled={!!alreadyAdded}
                      onClick={() => addApprover(u)}>
                      {alreadyAdded ? 'Added' : <><Plus className="h-3.5 w-3.5 mr-1" />Add</>}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800">
          <strong>Note:</strong> This is the <em>default</em> route. Individual contracts can have their
          approval route adjusted (approvers added or removed) before submission.
          The per-contract route always takes precedence.
        </div>
      </CollapsibleSection>

      <StageGroupsConfig />

      <ReviewStagesConfig />
    </AdminShell>
  );
}

const STAGES = [
  { id: 'INTERNAL_REVIEW', label: 'Internal Review' },
  { id: 'LEGAL_REVIEW', label: 'Legal Review' },
  { id: 'NEGOTIATION', label: 'Negotiation' },
  { id: 'FINAL', label: 'Final' },
  { id: 'SENT_FOR_SIGNATURE', label: 'Sent for Signature' },
];

function StageGroupsConfig() {
  const [stageGroups, setStageGroups] = useState({});
  const [availableGroups, setAvailableGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/contracts/admin-settings/stage-groups').then(r => {
      setStageGroups(r.data.stage_groups || {});
      setAvailableGroups(r.data.available_groups || []);
    }).catch(() => {});
  }, []);

  const setGroup = (stageId, groupCode) => {
    setStageGroups(prev => ({
      ...prev,
      [stageId]: groupCode ? { group_code: groupCode } : null,
    }));
  };

  const saveStageGroups = async () => {
    setSaving(true);
    try {
      await api.put('/api/contracts/admin-settings/stage-groups', { stage_groups: stageGroups });
      toast.success('Stage approval groups saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <CollapsibleSection title="Contract Review Stage Approval Groups" defaultOpen>
      <p className="text-sm text-muted-foreground mb-4">
        Assign an approval group to each review stage. The group's routing method (Any One / All Must Approve / Sequential) determines how approvals work at that stage.
      </p>
      <div className="space-y-3">
        {STAGES.map(stage => {
          const assigned = stageGroups[stage.id];
          const group = availableGroups.find(g => g.group_code === assigned?.group_code);
          return (
            <div key={stage.id} className="flex items-center gap-4 p-3 rounded-lg border bg-card">
              <div className="w-40">
                <p className="text-sm font-semibold">{stage.label}</p>
              </div>
              <select
                value={assigned?.group_code || ''}
                onChange={e => setGroup(stage.id, e.target.value)}
                className="flex-1 h-8 rounded-lg border border-input bg-background px-2 text-sm"
              >
                <option value="">No approval required</option>
                {availableGroups.map(g => (
                  <option key={g.group_code} value={g.group_code}>
                    {g.group_code} — {g.description} ({g.routing_method === 'all_at_once' ? 'All Must Approve' : g.routing_method === 'sequential' ? 'Sequential' : 'Any One'}, {(g.members || []).length} members)
                  </option>
                ))}
              </select>
              {group && (
                <Badge variant="outline" className="text-[10px] whitespace-nowrap">
                  {group.routing_method === 'all_at_once' ? 'All' : group.routing_method === 'sequential' ? 'Seq' : 'Any'} — {(group.members || []).length} members
                </Badge>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end mt-4">
        <Button onClick={saveStageGroups} disabled={saving}>{saving ? 'Saving...' : 'Save Stage Groups'}</Button>
      </div>
    </CollapsibleSection>
  );
}


function ReviewStagesConfig() {
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newStage, setNewStage] = useState('');

  useEffect(() => {
    api.get('/api/contracts/admin-settings/stage-groups').then(r => {
      setStages(r.data.review_stages || []);
    }).catch(() => {});
  }, []);

  const addStage = () => {
    if (!newStage.trim()) return;
    const id = newStage.trim().toUpperCase().replace(/\s+/g, '_');
    if (stages.some(s => s.id === id)) { toast.error('Stage already exists'); return; }
    setStages(prev => [...prev, { id, label: newStage.trim() }]);
    setNewStage('');
  };

  const removeStage = (idx) => {
    setStages(prev => prev.filter((_, i) => i !== idx));
  };

  const renameStage = (idx, newLabel) => {
    setStages(prev => prev.map((s, i) => i === idx ? { ...s, label: newLabel } : s));
  };

  const moveStage = (idx, dir) => {
    setStages(prev => {
      const arr = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[idx], arr[target]] = [arr[target], arr[idx]];
      return arr;
    });
  };

  const saveStages = async () => {
    setSaving(true);
    try {
      await api.put('/api/contracts/admin-settings/review-stages', { review_stages: stages });
      toast.success('Review stages saved');
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  return (
    <CollapsibleSection title="Contract Review Stages" defaultOpen>
      <p className="text-sm text-muted-foreground mb-4">
        Define the review stages for contract approval. Each client can have different stages.
        Stages appear in the order shown below. Draft and Executed are always present.
      </p>
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border">
          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold">1</div>
          <span className="text-sm font-medium text-muted-foreground">Draft</span>
          <span className="text-xs text-muted-foreground ml-auto">(always present)</span>
        </div>
        {stages.map((s, i) => (
          <div key={s.id} className="flex items-center gap-2 p-2 rounded-lg border bg-card">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">{i + 2}</div>
            <Input value={s.label} onChange={e => renameStage(i, e.target.value)} className="h-7 text-sm flex-1" />
            <div className="flex gap-0.5">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStage(i, -1)} disabled={i === 0}>
                <ChevronUp className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveStage(i, 1)} disabled={i === stages.length - 1}>
                <ChevronDown className="h-3 w-3" />
              </Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => removeStage(i)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30 border">
          <div className="w-6 h-6 rounded-full bg-green-200 flex items-center justify-center text-[10px] font-bold text-green-700">{stages.length + 2}</div>
          <span className="text-sm font-medium text-muted-foreground">Executed</span>
          <span className="text-xs text-muted-foreground ml-auto">(always present)</span>
        </div>
      </div>
      <div className="flex gap-2 mb-4">
        <Input value={newStage} onChange={e => setNewStage(e.target.value)} placeholder="New stage name..." className="h-8 text-sm" onKeyDown={e => e.key === 'Enter' && addStage()} />
        <Button size="sm" onClick={addStage} disabled={!newStage.trim()}><Plus className="h-3 w-3 mr-1" /> Add Stage</Button>
      </div>
      <div className="flex justify-end">
        <Button onClick={saveStages} disabled={saving}>{saving ? 'Saving...' : 'Save Review Stages'}</Button>
      </div>
    </CollapsibleSection>
  );
}
